import { GetStaticProps } from 'next';
import { useEffect, useState } from 'react';

import { ALL_MENU_QUERY, MenuLocationEnum } from '@/data/allMenuQuery';
import { flatListToHierarchical } from '@/utils/flatListToHierarchical';
import { getApolloClient } from '@/utils/apollo-client';
import { transformMenus } from '@/utils/transformMenus';
import { withLayout } from '@/layout/Layout';

import { LayoutProps } from '@/layout/Layout/Layout.props';
import { PageProps } from '@/types/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { Hero } from '@/components/common/Hero';
import Image from 'next/image';
import { Heading } from '@/components/typography';
import { Section } from '@/components/common/Section';
import { PHARMACEUTICAL_DEVELOPMENT_PAGE_QUERY } from '@/data/pharmaceuticalDevelopmentPageQuery';
import {
  FooterMenuLocationEnum,
  FOOTER_DATA_QUERY,
  FOOTER_DATA_QUERY_EN,
} from '@/data/footerDataQuery';
import { footerDataTransformer } from '@/utils/footerDataTransformer';
import { PageHead } from '@/components/common/PageHead';

const Page = ({ data }: PageProps & LayoutProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const {
    title,
    seo,
    featuredImage,
    pharmaceuticalDevelopment: { pharmaceutical },
  } = data.pageBy.translation;

  const featureImage = featuredImage?.node;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <>
      <PageHead
        title={seo?.title || title}
        seo={seo || {}}
        canonicalUrl={seo?.canonicalUrl}
        breadcrumbs={seo?.breadcrumbs}
      />
      {isMounted && (
        <>
          <Hero breadcrumbs={seo?.breadcrumbs}>
            <div className="relative pb-4 before:absolute before:bottom-0 before:left-0 before:h-[1px] before:w-full before:bg-primaryLight lg:flex">
              <Heading tag="h1" content={title || ''} />
              {featureImage && (
                <div className="hidden self-end lg:block">
                  <Image
                    src={featureImage?.mediaItemUrl ?? ''}
                    alt={featureImage?.altText ?? ''}
                    width={featureImage?.mediaDetails?.width}
                    height={featureImage?.mediaDetails?.height}
                  />
                </div>
              )}
            </div>
            <p
              className="lightHeroText"
              dangerouslySetInnerHTML={{ __html: pharmaceutical.description }}
            />
          </Hero>

          <Section className="py-15 md:!py-25">
            <div className="md:relative">
              <Heading
                tag="h2"
                className="accentText mb-1 text-h2Mobile md:absolute md:left-1/2 md:inline-block md:max-w-[45%] md:-translate-y-[15%] md:text-h3 lg:left-2/3 lg:max-w-[33%] lg:-translate-y-1/2"
                content={pharmaceutical.subtitle}
              />
              <ul className="pharmaGrid">
                {pharmaceutical.list.map((item, index) => (
                  <li key={item.item} className="pharmaGridItem">
                    <span className="pharmaGridItemIndex">
                      {'/0' + (index + 1)}
                    </span>
                    <p className="">{item.item}</p>
                  </li>
                ))}
              </ul>
            </div>
          </Section>
        </>
      )}
    </>
  );
};

export default withLayout(Page);

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const client = getApolloClient();
  const location =
    locale == 'uk'
      ? MenuLocationEnum.PRIMARY_NAVIGATION
      : MenuLocationEnum.PRIMARY_NAVIGATION___EN;
  const language = locale?.toUpperCase();
  const { data } = await client.query({
    query: PHARMACEUTICAL_DEVELOPMENT_PAGE_QUERY,
    variables: {
      language,
    },
  });
  const {
    data: { menuItems },
  } = await client.query({
    query: ALL_MENU_QUERY,
    variables: {
      location,
    },
  });
  const footerLocation =
    locale == 'uk'
      ? FooterMenuLocationEnum.FOOTER_MENU
      : FooterMenuLocationEnum.FOOTER_MENU___EN;

  const { data: footerMenuItems } =
    locale == 'uk'
      ? await client.query({
          query: FOOTER_DATA_QUERY,
          variables: {
            location: footerLocation,
          },
        })
      : await client.query({
          query: FOOTER_DATA_QUERY_EN,
          variables: {
            location: footerLocation,
          },
        });
  const footerData = footerDataTransformer(footerMenuItems);

  const flatList = flatListToHierarchical(menuItems.nodes);
  const menus = transformMenus(flatList);

  return {
    props: {
      data,
      menus,
      footerData,
      ...(await serverSideTranslations(locale as string, ['common'])),
    },
  };
};
